/* ═══════════════════════════════════════════════════════════════
   DAVID'S EMAIL REPLIES — Keyed by inbox IDs (sli1...sli5)
   Drafted by Sally for deal-facing notes and Luc for CEO/cross-fn.
   Consumed by V4EmailReply via V4SalesBriefing.
   ═══════════════════════════════════════════════════════════════ */
export const DAVID_EMAIL_REPLIES = {
  sli1: {
    agent: 'sally',
    subject: 'Re: NovaTech contract redlines',
    recipient: 'Raj Patel',
    thinkingTime: 8,
    thinkingSteps: [
      { label: 'Thinking...', detail: "Pulling NovaTech contract + redline history" },
      { label: 'Cross-referencing...', detail: 'Comparing 4.2 / 7.1 clauses against last 5 enterprise closes' },
      { label: 'Drafting response...', detail: 'Concede on 4.2, hold on 7.1, preserve Thursday close' },
    ],
    intro: "Raj is signaling green — tone was 'standard cleanup.' Concede clause 4.2 (we've done it 4 of the last 5 times with zero impact). Hold on 7.1 language — it's our boilerplate liability cap, non-negotiable. Keep the note short and unblock his Wednesday turnaround.",
    draft: `Hi Raj,

Thanks for the fast loop on legal's redlines — both are easy to address.

**Clause 4.2 (data retention):** Happy to meet you at 90 days. This aligns with how most of our enterprise customers run it, and our systems already support the shorter retention window. I'll ask our legal to redline the contract today.

**Clause 7.1 (liability cap):** This language is our standard liability cap across all enterprise deals — it's gone through 40+ legal reviews unchanged. I'd prefer to hold as-is. Happy to hop on a 15-min call with your legal if they want a walkthrough of the rationale.

We can have clean redlines back to you by Wednesday AM — keeps us on track for Thursday sign-off. Want me to loop our legal lead directly, or route through you?

Best,
David`,
    draftPrompt: "Reads like a deal-maker's note — concede what's cheap, hold what matters, keep the timeline. Send as-is, or tune the 7.1 pushback?",
    attachments: [
      { name: 'Standard_MSA_v4.1.pdf', type: 'pdf', size: '312 KB' },
    ],
    sentConfirm: "Sent. Raj's pattern is a 2-hour reply on legal threads. Expect him by ~10 AM. 4 more emails in the stack — keep going?",
    draftConfirm: "Saved. Redline call-outs preserved so you can pick up without re-reading 7.1.",
    remindConfirm: "Got it — I'll nudge you {time}. Don't let this drift past Wednesday AM or Thursday close slips.",
    editScenario: {
      userMsg: "Soften the 7.1 pushback — I don't want to come across as rigid. Frame it as 'happy to explain why' rather than 'non-negotiable'.",
      thinkingTime: 4,
      lucResponse: "Good call — Raj isn't their legal, he's the champion. Keep him feeling like a partner, not a blocker. Reframed 7.1 as a walk-through invitation.",
      revisedDraft: `Hi Raj,

Thanks for the fast loop on legal's redlines — both are easy to address.

**Clause 4.2 (data retention):** Happy to meet you at 90 days. This aligns with how most of our enterprise customers run it, and our systems already support the shorter retention window. I'll ask our legal to redline the contract today.

**Clause 7.1 (liability cap):** This language is our standard enterprise boilerplate — it's gone through 40+ legal reviews, which is why I'd love to keep it as-written if we can. I'm happy to set up a quick 15-min call between our legal teams so yours understands the reasoning. If there's a specific concern, I can route it back to our team for a proper look.

We can have clean redlines back to you by Wednesday AM — keeps us on track for Thursday sign-off. Want me to loop our legal lead directly, or route through you?

Best,
David`,
    },
  },

  sli2: {
    agent: 'sally',
    subject: 'Re: Meridian Health — implementation and pricing',
    recipient: 'Anita Desai',
    thinkingTime: 9,
    thinkingSteps: [
      { label: 'Thinking...', detail: "Pulling demo notes + Meridian company profile" },
      { label: 'Shaping proposal...', detail: 'Phased rollout recommendation for 85-employee ops team' },
      { label: 'Drafting response...', detail: 'Anchor pricing without a formal quote, lock next step' },
    ],
    intro: "Strong signal — she asked about implementation and team size, which means internal socialization has started. Anchor a phased rollout (ops team first, 45-day pilot, then company-wide), anchor a pricing range without quoting, and propose the next step as a working session, not another sales call.",
    draft: `Hi Anita,

Really glad the demo landed — your questions were exactly the right ones, which usually means an implementation will go smoothly.

**On implementation:** For an 85-person team, a typical path is a phased rollout — start with your ops team (the highest-impact use case from the demo), run a 45-day pilot with real workflows, then expand company-wide once your internal champions are comfortable. Most customers your size don't need a dedicated internal resource; a 2-hour/week point person during the pilot is enough.

**On team size:** Our 85-seat pricing tier lands in the $22K–$28K/year range depending on feature bundle and integration depth. I'd like to hold off on a formal quote until we've walked through which integrations actually matter for your workflows — that's where the price lands.

**Next step suggestion:** A 45-minute working session with me and a solutions engineer — we scope your top 3 workflows live, then send a quote that's anchored to your real use cases, not a generic template. I can do Thursday or Friday this week.

Which works better for you?

Best,
David`,
    draftPrompt: "This opens the door without pushing. Want to send as-is or adjust the price anchor?",
    attachments: [
      { name: 'Implementation_Phased_Approach.pdf', type: 'pdf', size: '1.1 MB' },
    ],
    sentConfirm: "Sent. Anita's pattern: she'll reply with a specific time — usually within 4 hours when she's motivated. 3 more emails in the stack. Keep going?",
    draftConfirm: "Saved. Pricing anchor + phased-rollout framing held — easy to revisit without re-writing.",
    remindConfirm: "Got it — nudging you {time}. Don't let the Meridian thread cool; that's our best SMB signal this quarter.",
    editScenario: {
      userMsg: "Drop the price range from the reply — too early. Let's keep her curious and use the working session to scope properly.",
      thinkingTime: 4,
      lucResponse: "Fair. Dropping the $22-28K anchor — we'll get a tighter number by understanding her workflows first. Less pre-commitment on our end, more signal on theirs.",
      revisedDraft: `Hi Anita,

Really glad the demo landed — your questions were exactly the right ones, which usually means an implementation will go smoothly.

**On implementation:** For an 85-person team, a typical path is a phased rollout — start with your ops team (the highest-impact use case from the demo), run a 45-day pilot with real workflows, then expand company-wide once your internal champions are comfortable. Most customers your size don't need a dedicated internal resource; a 2-hour/week point person during the pilot is enough.

**On pricing:** Rather than a generic quote, I'd rather get you a number that's anchored to your actual workflows. That lets us scope what you truly need — and skip what you don't.

**Next step suggestion:** A 45-minute working session with me and a solutions engineer — we scope your top 3 workflows live, then send a quote that's tailored to your real use cases. I can do Thursday or Friday this week.

Which works better for you?

Best,
David`,
    },
  },

  sli3: {
    agent: 'sally',
    subject: 'Re: TechCorp API issues — getting you a clean read',
    recipient: 'James Park',
    thinkingTime: 12,
    thinkingSteps: [
      { label: 'Thinking...', detail: "Pulling TechCorp ticket history + v2.3 API incident" },
      { label: 'Cross-referencing...', detail: 'Looping Stark for a written hotfix timeline' },
      { label: 'Drafting response...', detail: 'Empathetic, no-defensiveness, concrete commitments' },
    ],
    intro: "This is repair work, not a sales reply. Lead with empathy, own the failure without flinching, commit to a written timeline and a senior call, and — critically — don't pitch. Stark should back the timeline. If James sees effort without spin, the thread re-opens.",
    draft: `James,

Hearing you clearly — the v2.3 API breakage is ours to own, and the fact that it's landed right as you're evaluating is exactly the signal we should be giving better, not worse.

**What I can commit to today:**

**1. A call with Stark (our head of engineering) this week** — I'll offer Wednesday or Thursday, your choice. He'll walk you through the root cause, the hotfix plan, and the long-term testing changes we're putting in place so this doesn't repeat.

**2. A written timeline by EOD today** — I'll have Stark send it directly to you. API auth and data sync fixes are in the current sprint, hotfix deployment is set for Thursday. No marketing language, just the engineering plan and the dates.

**3. A standing line to me** — any blocker in the next 30 days, text me directly. I'll keep anything procedural out of your way.

On the AgentHub point — I hear you. I'm not going to counter-pitch. All I'd ask is that you hold on the final call until after the Stark conversation. If we can't earn the next 45 days, I understand the decision.

Talk soon,
David`,
    draftPrompt: "This is the right tone — no defensiveness, no sales-speak, just accountability and concrete commitments. Send as-is, or tighten?",
    attachments: [
      { name: 'v2.3_Incident_Report_Draft.pdf', type: 'pdf', size: '185 KB' },
    ],
    sentConfirm: "Sent. I've also pinged Stark to prepare the written timeline — he'll have it ready for you by 3 PM so you can forward it directly. 2 more emails to handle.",
    draftConfirm: "Saved. Note flagged as high-priority in your inbox — don't let this sit past today.",
    remindConfirm: "Nudging you {time} — this is the most important thread in your stack this week.",
    editScenario: {
      userMsg: "Add that Arjun (our CEO) is available for a direct call if James wants one. He needs to hear this matters at the top.",
      thinkingTime: 5,
      lucResponse: "Strong move. James is a CTO — a direct line from Arjun signals this isn't just engineering cleanup, it's leadership-level attention. Threaded it into point 1.",
      revisedDraft: `James,

Hearing you clearly — the v2.3 API breakage is ours to own, and the fact that it's landed right as you're evaluating is exactly the signal we should be giving better, not worse.

**What I can commit to today:**

**1. A call with Stark (our head of engineering) this week, and Arjun (our CEO) on the line if you want it** — Wednesday or Thursday, your choice. Stark will walk you through the root cause, the hotfix plan, and the long-term testing changes. Arjun joining signals that your renewal matters at the top, not just to me.

**2. A written timeline by EOD today** — I'll have Stark send it directly to you. API auth and data sync fixes are in the current sprint, hotfix deployment is set for Thursday. No marketing language, just the engineering plan and the dates.

**3. A standing line to me** — any blocker in the next 30 days, text me directly. I'll keep anything procedural out of your way.

On the AgentHub point — I hear you. I'm not going to counter-pitch. All I'd ask is that you hold on the final call until after the Stark conversation. If we can't earn the next 45 days, I understand the decision.

Talk soon,
David`,
    },
  },

  sli4: {
    agent: 'luc',
    subject: 'Re: Pipeline update for 3:30 sync',
    recipient: 'Arjun Mehta',
    thinkingTime: 7,
    thinkingSteps: [
      { label: 'Thinking...', detail: "Pulling revised Q1 forecast + slipped deals narrative" },
      { label: 'Shaping summary...', detail: 'Keeping this a 5-bullet read, not an essay' },
      { label: 'Drafting response...', detail: 'Aligning on format Arjun prefers before his Neel sync' },
    ],
    intro: "Arjun asked for bullets — he gets bullets. Revised Q1 number, two slipped deals (with why), SMB offset story, your confidence on the 3 late-stage SMBs, and one ask (TechCorp CEO outreach). Keep it to 5 bullets. He'll thank you for the brevity.",
    draft: `Arjun,

Short version for your 3:30 with Neel:

• **Q1 forecast revised to $342K** (down from $380K). Two enterprise slips account for the $38K gap.

• **Slippages:** TechCorp ($52K) — champion silent 12 days, AgentHub circling, re-engagement sequence queued. DataFlow ($38K) — CFO-led budget freeze until March, champion still warm, targeting Q2 reopen.

• **SMB offset:** Pipeline is +22% above Q1 target. Three late-stage deals (NovaTech, Meridian, CloudNine) representing ~$80K combined. Confidence on all three closing Q1: 85%+ on NovaTech (Thursday close expected), 60% on Meridian (in proposal scoping), 55% on CloudNine (mid-stage, expected late Feb).

• **Net gap:** $16K against original $380K plan. Bearable.

• **One ask:** Would you send a short CEO-level note to James Park (TechCorp CTO) this week? Sally has drafted a 3-line template — no pitch, just a signal that their renewal matters at the top. Changes the probability materially.

Happy to jump on a voice note version if that's easier for Neel. Let me know.

David`,
    draftPrompt: "Tight, scan-friendly, honest about the gap, ends with an ask. Send as-is or tune?",
    attachments: [
      { name: 'Q1_Revised_Forecast.xlsx', type: 'spreadsheet', size: '42 KB' },
    ],
    sentConfirm: "Sent. Arjun typically replies within 30 minutes on forecast threads. Expect confirmation on the TechCorp outreach ask by noon.",
    draftConfirm: "Saved. Format preserved so you can send without rework.",
    remindConfirm: "Got it — I'll nudge you {time}. The TechCorp ask is the swing point here.",
    editScenario: {
      userMsg: "Remove the 'voice note' line — Arjun will read the bullets, he doesn't need options. Keep it tight.",
      thinkingTime: 3,
      lucResponse: "Agreed. Dropping the closing line — the bullets do the work.",
      revisedDraft: `Arjun,

Short version for your 3:30 with Neel:

• **Q1 forecast revised to $342K** (down from $380K). Two enterprise slips account for the $38K gap.

• **Slippages:** TechCorp ($52K) — champion silent 12 days, AgentHub circling, re-engagement sequence queued. DataFlow ($38K) — CFO-led budget freeze until March, champion still warm, targeting Q2 reopen.

• **SMB offset:** Pipeline is +22% above Q1 target. Three late-stage deals (NovaTech, Meridian, CloudNine) representing ~$80K combined. Confidence on all three closing Q1: 85%+ on NovaTech (Thursday close expected), 60% on Meridian (in proposal scoping), 55% on CloudNine (mid-stage, expected late Feb).

• **Net gap:** $16K against original $380K plan. Bearable.

• **One ask:** Would you send a short CEO-level note to James Park (TechCorp CTO) this week? Sally has drafted a 3-line template — no pitch, just a signal that their renewal matters at the top. Changes the probability materially.

David`,
    },
  },

  sli5: {
    agent: 'sally',
    subject: 'Re: TechCorp case study — customer pick + ROI metrics',
    recipient: 'Priya Sharma',
    thinkingTime: 9,
    thinkingSteps: [
      { label: 'Thinking...', detail: "Pulling closed-won customers with similar profiles" },
      { label: 'Matching fit...', detail: 'Acme Corp — API-heavy enterprise, 200 FTE, similar stack' },
      { label: 'Drafting response...', detail: 'Confirming customer, ROI metrics, quote pathway' },
    ],
    intro: "Priya needs three things: which customer, what ROI metrics, and whether we can get a quote. Acme Corp is the clear pick — API-heavy, same size bracket as TechCorp, 40% workflow onboarding reduction is the headline metric. Lisa Park (Acme COO) is reachable and quote-friendly.",
    draft: `Hi Priya,

Happy to confirm everything you need:

**Customer pick: Acme Corp.**
Same company profile as TechCorp — API-heavy, enterprise, 200-person engineering org, same workflow tooling stack. The parallel is strong enough that the TechCorp team will recognize themselves in the story.

**ROI metrics to highlight:**
• **40% reduction in workflow onboarding time** (4 weeks → 2.4 weeks) — this is the flagship number
• **API integration time cut from 6 days to 1.5 days** on average, which maps to their exact pain point
• **$180K in developer-hours saved in Q1** — concrete dollar figure, the kind of number a CTO screenshots

**On the quote:**
Lisa Park (Acme COO) has already given us quote permission twice this year. I'll ping her today for a fresh 2-sentence quote that mirrors the 40% framing — should have it back to you by Tuesday EOD.

One more thought: Wright did a version of the Acme story as a blog post in November, but it wasn't packaged for outbound. Worth pulling those assets into the case study rather than starting from scratch.

Anything else for Wednesday's deadline?

Best,
David`,
    draftPrompt: "Confirms everything Priya asked, plus anticipates her next ask (the Wright asset reuse). Send as-is, or trim?",
    attachments: [
      { name: 'Acme_Corp_November_Blog_Post.pdf', type: 'pdf', size: '1.4 MB' },
    ],
    sentConfirm: "Sent. Priya typically kicks off the case-study packaging the same day she has the confirmation. Expect a Tuesday draft from her for your review.",
    draftConfirm: "Saved. Acme details + Wright asset pointer preserved.",
    remindConfirm: "Nudging you {time} — Priya's Wednesday deadline is hard to make if this slips past tomorrow.",
    editScenario: {
      userMsg: "Add a line that Sally can write a second version of the case study focused on the API integration angle — could be useful for TechCorp specifically.",
      thinkingTime: 4,
      lucResponse: "Good — differentiating by angle lets Priya deploy the same customer story across multiple touchpoints. Added the API-angle offer.",
      revisedDraft: `Hi Priya,

Happy to confirm everything you need:

**Customer pick: Acme Corp.**
Same company profile as TechCorp — API-heavy, enterprise, 200-person engineering org, same workflow tooling stack. The parallel is strong enough that the TechCorp team will recognize themselves in the story.

**ROI metrics to highlight:**
• **40% reduction in workflow onboarding time** (4 weeks → 2.4 weeks) — flagship number
• **API integration time cut from 6 days to 1.5 days** on average, which maps to their exact pain point
• **$180K in developer-hours saved in Q1** — concrete dollar figure, the kind of number a CTO screenshots

**On the quote:**
Lisa Park (Acme COO) has already given us quote permission twice this year. I'll ping her today for a fresh 2-sentence quote that mirrors the 40% framing — should have it back to you by Tuesday EOD.

**Bonus angle:** Sally can also write a second, shorter version of the case study focused just on the API integration story. That version would land specifically well with TechCorp (and any other API-heavy prospect). Happy to have her deliver both by Wednesday.

One more thought: Wright did a version of the Acme story as a blog post in November, but it wasn't packaged for outbound. Worth pulling those assets into the case study rather than starting from scratch.

Anything else for Wednesday's deadline?

Best,
David`,
    },
  },
};
